// src/pages/marketplace/developer/DeveloperDashboardPage.tsx
// Developer portal main dashboard for product owners.

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { StatusBadge } from '../../../components/StatusBadge';

/** Map product status to StatusBadge variant. */
function statusVariant(status: string): string {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'BETA':
      return 'info';
    case 'DRAFT':
      return 'secondary';
    case 'MAINTENANCE':
      return 'warning';
    case 'DEPRECATED':
      return 'error';
    default:
      return 'secondary';
  }
}

/** Render a simple star rating. */
function StarRating({ value }: { value: number }) {
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <span className="inline-flex items-center gap-0.5 text-amber-500">
      {'*'.repeat(full).split('').map((_, i) => (
        <svg key={`f${i}`} className="w-4 h-4 fill-current" viewBox="0 0 20 20">
          <path d="M10 15l-5.878 3.09 1.123-6.545L.49 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
        </svg>
      ))}
      {half && (
        <svg className="w-4 h-4" viewBox="0 0 20 20">
          <defs>
            <linearGradient id="half-star">
              <stop offset="50%" stopColor="currentColor" />
              <stop offset="50%" stopColor="transparent" />
            </linearGradient>
          </defs>
          <path fill="url(#half-star)" stroke="currentColor" strokeWidth="0.5" d="M10 15l-5.878 3.09 1.123-6.545L.49 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
        </svg>
      )}
      {'*'.repeat(empty).split('').map((_, i) => (
        <svg key={`e${i}`} className="w-4 h-4 text-muted-foreground/30" viewBox="0 0 20 20">
          <path fill="currentColor" d="M10 15l-5.878 3.09 1.123-6.545L.49 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
        </svg>
      ))}
    </span>
  );
}

export const DeveloperDashboardPage = observer(() => {
  const store = useProduct();
  const { t } = useI18n();

  useEffect(() => {
    store.ListProducts({
      pagination: { cursor: '', limit: 100, direction: 'forward' },
    });
  }, []);

  const items = store.items as any[];

  const stats = useMemo(() => {
    const totalProducts = items.length;
    const totalInstalls = items.reduce((sum: number, p: any) => sum + (p.install_count ?? 0), 0);
    const rated = items.filter((p: any) => p.rating != null && p.rating > 0);
    const avgRating = rated.length > 0
      ? rated.reduce((sum: number, p: any) => sum + (p.rating ?? 0), 0) / rated.length
      : 0;
    return { totalProducts, totalInstalls, avgRating };
  }, [items]);

  const handlePublishToggle = async (item: any) => {
    const id = item.entity?.id || item.id;
    if (!id) return;
    try {
      if (item.status === 'ACTIVE' || item.status === 'PRODUCT_STATUS_ACTIVE') {
        await store.SetProductStatus(id, { status: 'PRODUCT_STATUS_DRAFT' });
      } else {
        await store.PublishProduct(id, {});
      }
      store.ListProducts({
        pagination: { cursor: '', limit: 100, direction: 'forward' },
      });
    } catch {
      // Error handled by store
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header. The tab already names this page; the heading stays for screen readers. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="sr-only">Developer Portal</h1>
        <Link to="/developer/products/new" className="sm:ml-auto">
          <Button>Create New Product</Button>
        </Link>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">Total Products</p>
            <p className="text-3xl font-bold text-foreground mt-1">{stats.totalProducts}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">Total Installs</p>
            <p className="text-3xl font-bold text-foreground mt-1">{stats.totalInstalls.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">Average Rating</p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-3xl font-bold text-foreground">{stats.avgRating.toFixed(1)}</p>
              <StarRating value={stats.avgRating} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Products table */}
      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Installs</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableEmpty colSpan={6} message="No products yet. Create your first product." />
            ) : (
              items.map((item: any) => {
                const id = item.entity?.id || item.id;
                return (
                  <TableRow key={id || Math.random()}>
                    <TableCell>
                      <Link
                        to={`/developer/products/${id}/edit`}
                        className="flex items-center gap-3 text-sm font-medium text-primary hover:text-primary/80"
                      >
                        {item.icon_url ? (
                          <img
                            src={item.icon_url}
                            alt=""
                            className="w-8 h-8 rounded object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded bg-muted flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-muted-foreground">
                              {(item.name ?? 'P').charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <span>{item.name || item.slug || 'Untitled'}</span>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        variant={statusVariant(item.status ?? '')}
                        label={item.status || 'DRAFT'}
                        size="sm"
                      />
                    </TableCell>
                    <TableCell className="text-sm text-foreground">
                      {(item.install_count ?? 0).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-foreground">
                          {item.rating != null ? Number(item.rating).toFixed(1) : '-'}
                        </span>
                        {item.rating != null && item.rating > 0 && (
                          <StarRating value={item.rating} />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {item.created_at
                        ? new Date(item.created_at).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Link to={`/developer/products/${id}/edit`}>
                          <Button variant="ghost" size="sm">Edit</Button>
                        </Link>
                        <Link to={`/developer/products/${id}/marketplace`}>
                          <Button variant="ghost" size="sm">Marketplace</Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePublishToggle(item)}
                        >
                          {item.status === 'ACTIVE' ? 'Unpublish' : 'Publish'}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default DeveloperDashboardPage;
